import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  disablePortalModule,
  fetchEsignEnvelopeStatus,
  fetchModules,
  fetchSurveyStatus,
} from "../helpers/apiHelper";
import {
  applyLatestEmbeddedQuestionnaireStatuses,
  applyLatestEsignStatuses,
  getModuleInstanceKey,
  isEmbeddedQuestionnaireModule,
  isEsignDocumentModule,
  isModuleClosed,
  isSurveyStatusClosed,
  mergeEsignStatusIntoModule,
  mergeSurveyStatusIntoModule,
} from "../modules/moduleUtils";
import { useAuth } from "./AuthContext";
import {
  clearBootstrappedModules,
  getBootstrappedModules,
  getModulesBootstrappedEventName,
} from "./modulesBootstrapCache";

const ModulesContext = createContext(null);

const NO_OPEN_MATTER_STORAGE_KEY = "bhp:no_open_matter";

const getModuleToken = () => {
  const idToken =
    localStorage.getItem("idToken") || sessionStorage.getItem("idToken");
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  return accessToken || idToken || "";
};

// The eSign proxy routes only accept the Cognito access token, never the ID token.
const getAccessToken = () =>
  localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken") || "";

const getAuthFlow = () => {
  return localStorage.getItem("authFlow") || sessionStorage.getItem("authFlow") || "";
};

const hasNoOpenMatterFlag = () => {
  try {
    return (
      localStorage.getItem(NO_OPEN_MATTER_STORAGE_KEY) === "true" ||
      sessionStorage.getItem(NO_OPEN_MATTER_STORAGE_KEY) === "true"
    );
  } catch {
    return false;
  }
};

const MODULES_BOOTSTRAPPED_EVENT = getModulesBootstrappedEventName();

export const reconcileEmbeddedQuestionnaireModules = async (
  modules,
  token,
  fetchSurveyStatusFn = fetchSurveyStatus,
  disablePortalModuleFn = disablePortalModule,
) => {
  if (!Array.isArray(modules) || modules.length === 0 || !token) {
    return Array.isArray(modules) ? modules : [];
  }

  const invitationIds = Array.from(
    new Set(
      modules
        .filter((module) => isEmbeddedQuestionnaireModule(module) && module?.enabled === true)
        .filter((module) => !isModuleClosed(module))
        .map((module) => getModuleInstanceKey(module))
        .filter(Boolean),
    ),
  );

  const latestStatuses = new Map(
    await Promise.all(
      invitationIds.map(async (invitationId) => {
        try {
          return [invitationId, await fetchSurveyStatusFn(token, invitationId)];
        } catch (error) {
          console.error("Failed to reconcile survey status:", error);
          return [invitationId, null];
        }
      }),
    ),
  );

  const disabledModuleIds = new Set(
    (
      await Promise.all(
        modules.map(async (module) => {
          if (!isEmbeddedQuestionnaireModule(module) || module?.enabled !== true) {
            return null;
          }

          const invitationId = getModuleInstanceKey(module);
          const latestStatus = latestStatuses.get(invitationId);
          if (!invitationId || !(isSurveyStatusClosed(latestStatus) || isModuleClosed(module))) {
            return null;
          }

          try {
            const result = await disablePortalModuleFn(token, {
              ...(module?.id ? { moduleId: module.id } : {}),
              module: module.type,
              instanceKey: invitationId,
            });
            return result ? module.id : null;
          } catch (error) {
            console.error("Failed to disable completed embedded questionnaire:", error);
            return null;
          }
        }),
      )
    ).filter(Boolean),
  );

  return modules.map((module) => {
    if (!isEmbeddedQuestionnaireModule(module)) {
      return module;
    }

    const invitationId = getModuleInstanceKey(module);
    const latestStatus = latestStatuses.get(invitationId);

    if (!invitationId) {
      return module;
    }

    const reconciledModule = latestStatus
      ? mergeSurveyStatusIntoModule(module, latestStatus)
      : module;
    return disabledModuleIds.has(module.id)
      ? { ...reconciledModule, enabled: false }
      : reconciledModule;
  });
};

/**
 * Attaches the latest eSign status to each open `esign_document` module, so signed, declined,
 * expired or voided envelopes drop off the dashboard. Unlike questionnaires, nothing is written back:
 * the module row stays as provisioned and eSign remains the source of truth. A status that cannot be
 * read right now leaves the module unchanged.
 */
export const reconcileEsignDocumentModules = async (
  modules,
  accessToken,
  fetchEsignEnvelopeStatusFn = fetchEsignEnvelopeStatus,
) => {
  if (!Array.isArray(modules) || modules.length === 0 || !accessToken) {
    return Array.isArray(modules) ? modules : [];
  }

  const envelopeIds = Array.from(
    new Set(
      modules
        .filter((module) => isEsignDocumentModule(module) && module?.enabled === true)
        .filter((module) => !isModuleClosed(module))
        .map((module) => getModuleInstanceKey(module))
        .filter(Boolean),
    ),
  );

  if (envelopeIds.length === 0) {
    return modules;
  }

  const latestStatuses = new Map(
    await Promise.all(
      envelopeIds.map(async (envelopeId) => {
        try {
          return [envelopeId, await fetchEsignEnvelopeStatusFn(accessToken, envelopeId)];
        } catch (error) {
          console.error("Failed to reconcile eSign status:", error);
          return [envelopeId, null];
        }
      }),
    ),
  );

  return modules.map((module) => {
    if (!isEsignDocumentModule(module)) {
      return module;
    }

    const latestStatus = latestStatuses.get(getModuleInstanceKey(module));
    return latestStatus ? mergeEsignStatusIntoModule(module, latestStatus) : module;
  });
};

const overlayLatestStatuses = (currentModules, nextModules) =>
  applyLatestEsignStatuses(
    currentModules,
    applyLatestEmbeddedQuestionnaireStatuses(currentModules, nextModules),
  );

const reconcileModules = async (modules, moduleToken) =>
  reconcileEsignDocumentModules(
    await reconcileEmbeddedQuestionnaireModules(modules, moduleToken),
    getAccessToken(),
  );

export const ModulesProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesInitialized, setModulesInitialized] = useState(false);

  const modulesRefreshInFlightRef = useRef(null);
  const lastModulesRefreshAtRef = useRef(0);
  const modulesRef = useRef([]);
  modulesRef.current = modules;

  const fetchAuthoritativePortalModules = useCallback(async (token, seedModules) => {
    if (!token) {
      return null;
    }

    try {
      const fetchedModules = await fetchModules(token);
      const normalizedModules = Array.isArray(fetchedModules) ? fetchedModules : [];
      const overlaidModules = overlayLatestStatuses(
        Array.isArray(seedModules) ? seedModules : modulesRef.current,
        normalizedModules,
      );
      const nextModules = await reconcileModules(
        overlaidModules,
        token,
      );
      setModules(nextModules);
      setModulesInitialized(true);
      lastModulesRefreshAtRef.current = Date.now();
      return nextModules;
    } catch (err) {
      console.error("Authoritative module refresh failed:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) return;
    setModules([]);
    setModulesLoading(false);
    setModulesInitialized(false);
    clearBootstrappedModules();
    modulesRefreshInFlightRef.current = null;
    lastModulesRefreshAtRef.current = 0;
  }, [isLoggedIn]);

  const loadModules = useCallback(async ({ clearOnError = true } = {}) => {
    if (getAuthFlow() === "portal_user" && hasNoOpenMatterFlag()) {
      setModules([]);
      setModulesInitialized(true);
      return [];
    }

    if (getAuthFlow() === "portal_user") {
      const moduleToken = getModuleToken();
      const bootstrapModules = getBootstrappedModules();

      if (Array.isArray(bootstrapModules)) {
        const overlaidModules = overlayLatestStatuses(
          modulesRef.current,
          bootstrapModules,
        );
        const nextModules = await reconcileModules(
          overlaidModules,
          moduleToken,
        );
        setModules(nextModules);
        setModulesInitialized(true);
        lastModulesRefreshAtRef.current = Date.now();
        void fetchAuthoritativePortalModules(moduleToken, nextModules);
        return nextModules;
      }

      return new Promise((resolve) => {
        const handleBootstrappedModules = (event) => {
          // Detach before the (possibly slow) status reconcile, so later bootstrap events cannot
          // start parallel reconciles while this one is still waiting.
          window.removeEventListener(
            MODULES_BOOTSTRAPPED_EVENT,
            handleBootstrappedModules,
          );
          const run = async () => {
            const bootstrappedModules = Array.isArray(event?.detail) ? event.detail : [];
            const overlaidModules = overlayLatestStatuses(
              modulesRef.current,
              bootstrappedModules,
            );
            const nextModules = await reconcileModules(
              overlaidModules,
              moduleToken,
            );
            setModules(nextModules);
            setModulesInitialized(true);
            lastModulesRefreshAtRef.current = Date.now();
            void fetchAuthoritativePortalModules(moduleToken, nextModules);
            resolve(nextModules);
          };

          run().catch((error) => {
            console.error("Module bootstrap reconcile failed:", error);
            const fallbackModules = Array.isArray(event?.detail) ? event.detail : [];
            setModules(fallbackModules);
            setModulesInitialized(true);
            resolve(fallbackModules);
          });
        };

        window.addEventListener(
          MODULES_BOOTSTRAPPED_EVENT,
          handleBootstrappedModules,
        );
      });
    }

    const moduleToken = getModuleToken();

    if (!moduleToken) {
      setModules([]);
      setModulesInitialized(true);
      return [];
    }

    setModulesLoading(true);
    try {
      const fetchedModules = await fetchModules(moduleToken);
      const normalizedModules = Array.isArray(fetchedModules) ? fetchedModules : [];
      const overlaidModules = overlayLatestStatuses(
        modulesRef.current,
        normalizedModules,
      );
      const nextModules = await reconcileModules(
        overlaidModules,
        moduleToken,
      );
      setModules(nextModules);
      setModulesInitialized(true);
      lastModulesRefreshAtRef.current = Date.now();
      return nextModules;
    } catch (err) {
      console.error("Module bootstrap failed:", err);
      if (clearOnError) {
        setModules([]);
      }
      setModulesInitialized(true);
      return null;
    } finally {
      setModulesLoading(false);
    }
  }, [fetchAuthoritativePortalModules]);

  const refreshModules = useCallback(
    async () => {
      if (!isLoggedIn) return [];
      if (getAuthFlow() === "portal_user" && hasNoOpenMatterFlag()) {
        setModules([]);
        setModulesInitialized(true);
        return [];
      }

      if (modulesRefreshInFlightRef.current) {
        return modulesRefreshInFlightRef.current;
      }

      const refreshPromise = (async () => {
        if (getAuthFlow() === "portal_user") {
          const moduleToken = getModuleToken();

          if (!moduleToken) {
            return [];
          }

          setModulesLoading(true);
          try {
            return await fetchAuthoritativePortalModules(
              moduleToken,
              modulesRef.current,
            );
          } catch (err) {
            console.error("Module refresh failed:", err);
            return null;
          } finally {
            setModulesLoading(false);
          }
        }

        return loadModules({ clearOnError: false });
      })()
        .then((result) => {
          if (Array.isArray(result)) {
            lastModulesRefreshAtRef.current = Date.now();
            return result;
          }
          return modules;
        })
        .finally(() => {
          modulesRefreshInFlightRef.current = null;
        });

      modulesRefreshInFlightRef.current = refreshPromise;
      return refreshPromise;
    },
    [fetchAuthoritativePortalModules, isLoggedIn, loadModules, modules],
  );

  useEffect(() => {
    if (!isLoggedIn || modulesInitialized) return;
    loadModules({ clearOnError: false });
  }, [isLoggedIn, modulesInitialized, loadModules]);

  const value = useMemo(
    () => ({
      modules,
      setModules,
      modulesLoading,
      modulesInitialized,
      loadModules,
      refreshModules,
    }),
    [modules, modulesLoading, modulesInitialized, loadModules, refreshModules],
  );

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
};

export const useModules = () => useContext(ModulesContext);
